import { EventScope } from '@openticket/lib-eventbus';
/**
 * Initializes a new Google Tag Manager container and
 * event bus. Listen and parses a subset of SDK events
 * to send to GTM
 */
export default class GoogleTagManager extends EventScope {
    constructor(id, eventBus, increment) {
        super(eventBus);
        this._id = `${increment}`;
        // Load Google Tag Manager js
        const w = window;
        const d = document;
        const s = 'script';
        const l = `dataLayer_${increment}`;
        // @ts-ignore
        w[l] = w[l] || [];
        // @ts-ignore
        w[l].push({
            'gtm.start': new Date().getTime(),
            event: 'gtm.js',
        });
        const f = d.getElementsByTagName(s)[0];
        const j = d.createElement(s);
        const dl = l != 'dataLayer' ? '&l=' + l : '';
        j.async = true;
        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + id + dl;
        // @ts-ignore
        f.parentNode.insertBefore(j, f);
        // Insert Google Tag Manager IFrame
        const noscript = document.createElement('noscript');
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${id}`;
        iframe.height = '0';
        iframe.width = '0';
        // @ts-ignore
        iframe.style = 'display:none;visibility:hidden';
        noscript.appendChild(iframe);
        const body = document.body;
        body.insertBefore(noscript, body.firstChild);
        // Set event listeners
        this.on('shop', (path, data) => this._pushCompat(path, data));
        this.on('cart', (path, data) => this._pushCompat(path, data));
        this.on('router', (path, data) => this._pushCompat(path, data));
        // Fire this event on init
        this.dataLayer.push(GoogleTagManagerCompat.contentView(window.location.pathname));
    }
    /**
     * Push custom data to GTM, while bypassing the event bus
     */
    push(data) {
        this.dataLayer.push(Object.assign(Object.assign({}, data), { event: `ot-${data.event}` }));
    }
    /**
     * Replicates the old shop to push the same events
     * for backwards compatibility with the GTM container
     * @param path - the event path
     * @param data - the event data
     */
    _pushCompat(path, data) {
        // Array of messages to push
        let messages = [];
        // Ignore timer events
        if (path[1] === 'timer') {
            return;
        }
        //  Decide what message should be generated
        switch (path.shift()) {
            case 'cart':
                switch (path.shift()) {
                    case 'ticket':
                        // Remove the guid from the path, as it is not relevant here
                        path.shift();
                        switch (path.shift()) {
                            case 'add':
                                messages = [GoogleTagManagerCompat.cartEvent('add', 'products', data)];
                                break;
                            case 'remove':
                                messages = [GoogleTagManagerCompat.cartEvent('remove', 'products', data)];
                                break;
                        }
                        break;
                }
                break;
            case 'router':
                messages = [
                    GoogleTagManagerCompat.contentView(window.location.pathname),
                    GoogleTagManagerCompat.history(data.new, data.old)
                ];
                break;
            case 'shop':
                switch (path.shift()) {
                    case 'impression':
                        switch (path.shift()) {
                            case 'filter':
                                switch (path.shift()) {
                                    case 'events':
                                        messages = [GoogleTagManagerCompat.impression(data.shop, data.items)];
                                        break;
                                }
                                break;
                            case 'details':
                                messages = [
                                    GoogleTagManagerCompat.details(data.shop, data.items),
                                    GoogleTagManagerCompat.checkout(data.shop, data.items, 1),
                                ];
                                break;
                            case 'payment':
                                messages = [
                                    GoogleTagManagerCompat.checkout(data.shop, data.items, 2),
                                ];
                                break;
                        }
                        break;
                }
                break;
        }
        // Loop over the messages and push them to GTM
        for (const message of messages) {
            this.dataLayer.push(message);
        }
    }
    /**
     * dataLayer getter to only deal with the ts-ignore in one place
     */
    get dataLayer() {
        // @ts-ignore
        return window[`dataLayer_${this._id}`];
    }
}
/**
 * The GTM compatibility class, these functions create events
 * with the same sructure and data as the old shop
 */
class GoogleTagManagerCompat {
    /**
     * Triggered on router change
     */
    static contentView(path) {
        return {
            event: 'content-view',
            'content-name': path,
        };
    }
    /**
     * Triggered when an event is selected
     */
    static impression(shop, items) {
        return {
            event: 'impression',
            ecommerce: {
                impressions: items.map((item) => {
                    return Object.assign(Object.assign({}, item), { price: (item.price / 100).toFixed(2), list: shop.name });
                }),
            },
        };
    }
    /**
     * Triggered for every route change, containing the
     * current and the previous route
     */
    static history(to, from) {
        return {
            event: 'gtm.historyChange',
            'gtm.newUrl': to,
            'gtm.oldUrl': from,
        };
    }
    /**
     * Triggered for cart add and remove events on tickets
     */
    static cartEvent(action, scope, event) {
        let price = 0;
        for (const item of event.items) {
            price += item.pricing.total_price;
        }
        return {
            event: action === 'add' ? 'addToCart' : 'removeFromCart',
            ecommerce: {
                [action]: {
                    [scope]: [{
                            id: event.guid,
                            name: event.name,
                            price: (price / 100).toFixed(2),
                            quantity: event.items.length,
                        }],
                },
            },
        };
    }
    /**
     * Triggered when the booker information becomes visible
     */
    static details(shop, items) {
        return {
            event: 'detail',
            ecommerce: {
                detail: {
                    actionField: {
                        list: shop.name,
                        action: 'detail',
                    },
                    products: items.map((item) => {
                        return Object.assign(Object.assign({}, item), { price: (item.price / 100).toFixed(2), quantity: 1, list: shop.name });
                    }),
                },
            },
        };
    }
    /**
     * Triggered when the booker information becomes visible and again
     * when the payment method overview becomes visible
     */
    static checkout(shop, items, step) {
        return {
            event: 'checkout',
            ecommerce: {
                detail: {
                    actionField: {
                        step,
                        list: shop.name,
                        action: 'checkout',
                    },
                    products: items.map((item) => {
                        return Object.assign(Object.assign({}, item), { price: (item.price / 100).toFixed(2), quantity: 1, list: shop.name });
                    }),
                },
            },
        };
    }
}
//# sourceMappingURL=googleTagManager.js.map